import { useEffect, useRef, useState } from "react";
import { ArrowUpDown, ChevronDown, ChevronRight, ChevronUp, ExternalLink, Layers, List, SlidersHorizontal } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function formatDateSafe(isoStr) {
  if (!isoStr) return null;
  const datePart = isoStr.split("T")[0];
  if (!datePart) return isoStr;
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
}

// ── Columns (no "Company" — we already know the company) ──────────────────────

const COLUMNS = [
  { key: "deal_name",         label: "Deal",        sortable: true, alwaysVisible: true  },
  { key: "product",           label: "Product",     sortable: true, alwaysVisible: false },
  { key: "line_item_name",    label: "Line item",   sortable: true, alwaysVisible: false },
  { key: "line_item_price",   label: "Price",       sortable: true, alwaysVisible: false, numeric: true },
  { key: "monthly_mrr",       label: "MRR",         sortable: true, alwaysVisible: true,  numeric: true },
  { key: "billing_frequency", label: "Billing",     sortable: true, alwaysVisible: false },
  { key: "deal_stage",        label: "Stage",       sortable: true, alwaysVisible: false },
  { key: "csm",               label: "CSM",         sortable: true, alwaysVisible: false },
  { key: "owner",             label: "Owner",       sortable: true, alwaysVisible: false },
];

const ALL_COLUMN_KEYS = COLUMNS.map((c) => c.key);
const STORAGE_KEY = "portaliq_company_drilldown_columns";

// Mirror the same default set as the MRR drilldown
const DEFAULT_COLUMN_KEYS = [
  "deal_name", "product", "line_item_name",
  "line_item_price", "monthly_mrr", "deal_stage", "csm",
];

function loadSavedColumns() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_COLUMN_KEYS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_COLUMN_KEYS;
    const alwaysVisible = COLUMNS.filter((c) => c.alwaysVisible).map((c) => c.key);
    return [...new Set([...alwaysVisible, ...parsed.filter((k) => ALL_COLUMN_KEYS.includes(k))])];
  } catch {
    return DEFAULT_COLUMN_KEYS;
  }
}

// ── Grouping helper ───────────────────────────────────────────────────────────

function groupByDeal(records) {
  const map = new Map();
  for (const r of records) {
    const key = r.deal_id ?? r.deal_name;
    if (!map.has(key)) {
      map.set(key, {
        deal_id: r.deal_id,
        deal_name: r.deal_name,
        deal_url: r.deal_url,
        deal_stage: r.deal_stage,
        owner: r.owner,
        total_mrr: 0,
        items: [],
      });
    }
    const g = map.get(key);
    g.total_mrr = Math.round((g.total_mrr + (r.monthly_mrr || 0)) * 100) / 100;
    g.items.push(r);
  }
  return [...map.values()];
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function SortIcon({ active, dir }) {
  if (!active) return <ArrowUpDown className="ml-1 inline h-3 w-3 text-slate-300" />;
  return dir === "asc"
    ? <ChevronUp className="ml-1 inline h-3 w-3 text-[#119996]" />
    : <ChevronDown className="ml-1 inline h-3 w-3 text-[#119996]" />;
}

function ColumnPicker({ visibleColumns, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (key) => {
    if (COLUMNS.find((c) => c.key === key)?.alwaysVisible) return;
    onChange(visibleColumns.includes(key) ? visibleColumns.filter((k) => k !== key) : [...visibleColumns, key]);
  };

  const hiddenCount = COLUMNS.filter((c) => !c.alwaysVisible && !visibleColumns.includes(c.key)).length;

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ${open ? "border-[#57c1a9] bg-[#e8faf5] text-[#119996]" : "border-[#dddddd] bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}>
        <SlidersHorizontal className="h-3.5 w-3.5" />
        Columns
        {hiddenCount > 0 && <span className="rounded-full bg-[#50e3c2] px-1.5 py-px text-[10px] font-semibold text-[#323c44]">{hiddenCount}</span>}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-md border border-[#dddddd] bg-white py-1 shadow-lg">
          <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7d8b96]">Visible columns</p>
          {COLUMNS.map((col) => (
            <label key={col.key} className={`flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm hover:bg-[#f5f5f5] ${col.alwaysVisible ? "cursor-default opacity-50" : ""}`}>
              <input type="checkbox" checked={visibleColumns.includes(col.key)} disabled={col.alwaysVisible} onChange={() => toggle(col.key)} className="h-3.5 w-3.5 accent-[#50e3c2]" />
              <span className="text-[#323c44]">{col.label}</span>
              {col.alwaysVisible && <span className="ml-auto text-[10px] text-[#7d8b96]">Always</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Grouped table ─────────────────────────────────────────────────────────────

function GroupedTable({ groups, expandedDeals, onToggle, formatCurrency, sortKey, sortDir, onSort, activeColumns }) {

  function renderGroupCell(col, group) {
    const firstItem = group.items[0];
    const isSingle  = group.items.length === 1;

    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="min-w-[200px]">
            <div className="flex flex-wrap items-center gap-1">
              <a href={group.deal_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 font-medium text-[#323c44] underline-offset-4 hover:text-[#119996] hover:underline"
                onClick={(e) => e.stopPropagation()}>
                {group.deal_name}
                <ExternalLink className="h-3 w-3" />
              </a>
              {group.items.length > 1 && (
                <span className="rounded-full bg-[#efefef] px-1.5 py-0.5 text-[10px] font-semibold text-[#4a5b68]">
                  {group.items.length} items
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-[#7d8b96]">ID {group.deal_id}</p>
          </TableCell>
        );
      case "product":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{isSingle ? (firstItem?.product || "—") : "—"}</TableCell>;
      case "line_item_name":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{isSingle ? (firstItem?.line_item_name || "—") : "—"}</TableCell>;
      case "line_item_price":
        return <TableCell key={col.key} className="font-mono text-sm text-[#4a5b68]">{isSingle ? formatCurrency(firstItem?.line_item_price) : "—"}</TableCell>;
      case "monthly_mrr":
        return (
          <TableCell key={col.key} className="text-right font-mono font-semibold text-[#323c44]">
            {formatCurrency(group.total_mrr)}
          </TableCell>
        );
      case "billing_frequency":
        return (
          <TableCell key={col.key} className="text-xs text-[#4a5b68]">
            {isSingle ? (
              <>
                <p>{firstItem?.billing_frequency}</p>
                <p className="text-[#7d8b96]">
                  {firstItem?.billing_start_date ? formatDateSafe(firstItem.billing_start_date) : "—"}
                  {" – "}
                  {firstItem?.billing_end_date ? formatDateSafe(firstItem.billing_end_date) : "Open"}
                </p>
              </>
            ) : "—"}
          </TableCell>
        );
      case "deal_stage":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.deal_stage || "—"}</TableCell>;
      case "csm":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{firstItem?.csm || "—"}</TableCell>;
      case "owner":
        return <TableCell key={col.key} className="text-sm text-[#4a5b68]">{group.owner || "—"}</TableCell>;
      default:
        return <TableCell key={col.key}>—</TableCell>;
    }
  }

  function renderSubCell(col, item) {
    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="py-2 pl-6">
            <p className="text-sm font-medium text-[#323c44]">{item.line_item_name}</p>
            <p className="text-xs text-[#7d8b96]">{item.product}</p>
          </TableCell>
        );
      case "product":        return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.product || "—"}</TableCell>;
      case "line_item_name": return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.line_item_name || "—"}</TableCell>;
      case "line_item_price":return <TableCell key={col.key} className="py-2 font-mono text-sm text-[#4a5b68]">{formatCurrency(item.line_item_price)}</TableCell>;
      case "monthly_mrr":
        return (
          <TableCell key={col.key} className="py-2 text-right font-mono text-sm text-[#323c44]">
            {formatCurrency(item.monthly_mrr)}
          </TableCell>
        );
      case "billing_frequency":
        return (
          <TableCell key={col.key} className="py-2 text-xs text-[#4a5b68]">
            <p>{item.billing_frequency}</p>
            <p className="text-[#7d8b96]">
              {item.billing_start_date ? formatDateSafe(item.billing_start_date) : "—"}
              {" – "}
              {item.billing_end_date ? formatDateSafe(item.billing_end_date) : "Open"}
            </p>
          </TableCell>
        );
      case "deal_stage": return <TableCell key={col.key} className="py-2" />;
      case "csm":        return <TableCell key={col.key} className="py-2 text-sm text-[#4a5b68]">{item.csm || "—"}</TableCell>;
      case "owner":      return <TableCell key={col.key} className="py-2" />;
      default:           return <TableCell key={col.key} className="py-2" />;
    }
  }

  return (
    <div className="overflow-hidden rounded-md border border-[#cccccc]" data-testid="company-drilldown-grouped-table">
      <Table>
        <TableHeader className="bg-[#f5f5f5]">
          <TableRow>
            <TableHead className="w-8" />
            {activeColumns.map((col) => (
              <TableHead
                key={col.key}
                className={col.sortable ? "cursor-pointer select-none hover:bg-[#efefef]" : ""}
                onClick={col.sortable ? () => onSort(col.key) : undefined}
              >
                <span className="whitespace-nowrap">
                  {col.label}
                  {col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isExpanded  = expandedDeals.has(group.deal_id ?? group.deal_name);
            const hasMultiple = group.items.length > 1;
            return (
              <>
                <TableRow
                  key={`deal-${group.deal_id}`}
                  className="cursor-pointer hover:bg-[#f5f5f5]"
                  onClick={() => hasMultiple && onToggle(group.deal_id ?? group.deal_name)}
                  data-testid={`company-drilldown-group-${group.deal_id}`}
                >
                  <TableCell className="w-8 pr-0 text-center">
                    {hasMultiple ? (
                      <span className="text-[#7d8b96]">
                        {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      </span>
                    ) : <span className="block h-3.5 w-3.5" />}
                  </TableCell>
                  {activeColumns.map((col) => renderGroupCell(col, group))}
                </TableRow>

                {isExpanded && group.items.map((item, idx) => (
                  <TableRow key={`item-${group.deal_id}-${idx}`} className="bg-[#f5f5f5] hover:bg-[#f0f6ff]"
                    data-testid={`company-drilldown-item-${group.deal_id}-${idx}`}>
                    <TableCell className="w-8 border-l-2 border-[#57c1a9]/20 py-2" />
                    {activeColumns.map((col) => renderSubCell(col, item))}
                  </TableRow>
                ))}
              </>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CompanyDrilldownSheet({
  open,
  onOpenChange,
  company,
  companyMrr,
  companyArr,
  detail,
  loading,
  formatCurrency,
  selectedMonth,
  onMonthChange,
  lockToCompany,
  onLockToCompanyChange,
  appliedFiltersDescription,
}) {
  const [sortKey, setSortKey] = useState("monthly_mrr");
  const [sortDir, setSortDir] = useState("desc");
  const [visibleColumns, setVisibleColumns] = useState(loadSavedColumns);
  const [groupedMode, setGroupedMode] = useState(true);
  const [expandedDeals, setExpandedDeals] = useState(new Set());

  const handleColumnsChange = (next) => {
    setVisibleColumns(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const toggleDealExpand = (dealId) => {
    setExpandedDeals((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId); else next.add(dealId);
      return next;
    });
  };

  const records = detail?.records || [];

  const sortedRecords = [...records].sort((a, b) => {
    let av = a[sortKey] ?? ""; let bv = b[sortKey] ?? "";
    if (typeof av === "number" && typeof bv === "number") return sortDir === "asc" ? av - bv : bv - av;
    av = String(av).toLowerCase(); bv = String(bv).toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const sortedGroups = groupByDeal(records).sort((a, b) => {
    const aVal = sortKey === "monthly_mrr" ? a.total_mrr : (a[sortKey] ?? "");
    const bVal = sortKey === "monthly_mrr" ? b.total_mrr : (b[sortKey] ?? "");
    if (typeof aVal === "number" && typeof bVal === "number") return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    const as = String(aVal).toLowerCase(); const bs = String(bVal).toLowerCase();
    if (as < bs) return sortDir === "asc" ? -1 : 1;
    if (as > bs) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const uniqueDealCount = new Set(records.map((r) => r.deal_id)).size;
  const activeColumns   = COLUMNS.filter((c) => visibleColumns.includes(c.key));
  const isEmpty         = groupedMode ? sortedGroups.length === 0 : sortedRecords.length === 0;

  function renderCell(col, record) {
    switch (col.key) {
      case "deal_name":
        return (
          <TableCell key={col.key} className="min-w-[210px]">
            <a href={record.deal_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 font-medium text-[#323c44] underline-offset-4 hover:text-[#119996] hover:underline">
              {record.deal_name} <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <p className="mt-1 text-xs text-[#4a5b68]">ID {record.deal_id}</p>
          </TableCell>
        );
      case "product":        return <TableCell key={col.key}>{record.product || "—"}</TableCell>;
      case "line_item_name": return <TableCell key={col.key}>{record.line_item_name}</TableCell>;
      case "line_item_price":return <TableCell key={col.key} className="font-mono">{formatCurrency(record.line_item_price)}</TableCell>;
      case "monthly_mrr":    return <TableCell key={col.key} className="font-mono text-[#323c44]">{formatCurrency(record.monthly_mrr)}</TableCell>;
      case "billing_frequency":
        return (
          <TableCell key={col.key}>
            <div className="space-y-1 text-xs text-[#4a5b68]">
              <p>{record.billing_frequency}</p>
              <p>{record.billing_start_date ? formatDateSafe(record.billing_start_date) : "—"}</p>
              <p>{record.billing_end_date ? formatDateSafe(record.billing_end_date) : "Open ended"}</p>
            </div>
          </TableCell>
        );
      case "deal_stage": return <TableCell key={col.key}>{record.deal_stage || "—"}</TableCell>;
      case "csm":        return <TableCell key={col.key}>{record.csm || "—"}</TableCell>;
      case "owner":      return <TableCell key={col.key}>{record.owner || "—"}</TableCell>;
      default:           return null;
    }
  }

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) setExpandedDeals(new Set()); onOpenChange(v); }}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l border-[#cccccc] bg-white p-0 sm:max-w-[78rem]" data-testid="company-drilldown-sheet">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 border-b border-[#cccccc] bg-[#f5f5f5] px-6 py-5">
          <SheetHeader>
            <SheetTitle className="font-sans text-2xl text-[#323c44]" data-testid="company-drilldown-title">
              {company || "Company"}
            </SheetTitle>
            <SheetDescription className="text-sm text-[#4a5b68]">
              Active line items for {detail?.label ?? selectedMonth}
              {appliedFiltersDescription ? ` — ${appliedFiltersDescription}` : ""}
            </SheetDescription>
          </SheetHeader>

          {/* Stat cards */}
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-[#cccccc] bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Company MRR</p>
              <p className="mt-3 font-sans text-3xl text-[#323c44]">{formatCurrency(companyMrr ?? 0)}</p>
            </div>
            <div className="rounded-md border border-[#cccccc] bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Company ARR</p>
              <p className="mt-3 font-sans text-3xl text-[#323c44]">{formatCurrency(companyArr ?? 0)}</p>
            </div>
            <div className="rounded-md border border-[#cccccc] bg-white p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#4a5b68]">Deals this month</p>
              <p className="mt-3 font-sans text-3xl text-[#323c44]">{uniqueDealCount}</p>
            </div>
          </div>

          {/* Month picker + lock toggle */}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[#323c44]">
              <span className="font-medium">Viewing month:</span>
              <input
                type="month"
                value={selectedMonth ?? ""}
                onChange={(e) => onMonthChange?.(e.target.value)}
                className="rounded-md border border-[#cccccc] bg-white px-2 py-1 text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
              />
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[#323c44]">
              <input type="checkbox" checked={lockToCompany ?? false} onChange={(e) => onLockToCompanyChange?.(e.target.checked)} className="h-3.5 w-3.5 accent-[#50e3c2]" />
              Lock to company
            </label>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {loading ? (
            <div className="rounded-md border border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">
              Loading line items for {company}...
            </div>
          ) : detail ? (
            isEmpty ? (
              <div className="rounded-md border border-dashed border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">
                No active line items found for {company} in this month.
              </div>
            ) : (
              <>
                {/* Toolbar */}
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-xs text-[#7d8b96]">
                    {groupedMode
                      ? `${sortedGroups.length} deal${sortedGroups.length !== 1 ? "s" : ""}, ${records.length} line item${records.length !== 1 ? "s" : ""}`
                      : `${records.length} record${records.length !== 1 ? "s" : ""}`}
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex overflow-hidden rounded-md border border-[#dddddd]">
                      <button type="button" onClick={() => setGroupedMode(true)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${groupedMode ? "bg-[#e8faf5] text-[#119996]" : "bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}>
                        <Layers className="h-3.5 w-3.5" /> Grouped
                      </button>
                      <button type="button" onClick={() => setGroupedMode(false)}
                        className={`flex items-center gap-1.5 border-l border-[#dddddd] px-3 py-1.5 text-xs font-medium transition ${!groupedMode ? "bg-[#e8faf5] text-[#119996]" : "bg-white text-[#4a5b68] hover:bg-[#efefef]"}`}>
                        <List className="h-3.5 w-3.5" /> Flat
                      </button>
                    </div>
                    {/* Columns picker — available in both modes */}
                    <ColumnPicker visibleColumns={visibleColumns} onChange={handleColumnsChange} />
                  </div>
                </div>

                {groupedMode ? (
                  <GroupedTable
                    groups={sortedGroups}
                    expandedDeals={expandedDeals}
                    onToggle={toggleDealExpand}
                    formatCurrency={formatCurrency}
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                    activeColumns={activeColumns}
                  />
                ) : (
                  <div className="overflow-hidden rounded-md border border-[#cccccc]">
                    <Table>
                      <TableHeader className="bg-[#f5f5f5]">
                        <TableRow>
                          {activeColumns.map((col) => (
                            <TableHead key={col.key} className={col.sortable ? "cursor-pointer select-none hover:bg-[#efefef]" : ""} onClick={col.sortable ? () => handleSort(col.key) : undefined}>
                              <span className="whitespace-nowrap">{col.label}{col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}</span>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedRecords.map((record) => (
                          <TableRow key={`${record.deal_id}-${record.line_item_name}-${record.product}`}>
                            {activeColumns.map((col) => renderCell(col, record))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="rounded-md border border-[#cccccc] bg-[#f5f5f5] px-6 py-16 text-center text-sm text-[#4a5b68]">
              Select a company to inspect its contributing line items.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
